import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, Building2, Clock, Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  login: 'bg-blue-500/20 text-blue-400',
  created: 'bg-emerald-500/20 text-emerald-400',
  edited: 'bg-amber-500/20 text-amber-400',
  deleted: 'bg-red-500/20 text-red-400',
  status_change: 'bg-violet-500/20 text-violet-400',
  exported: 'bg-cyan-500/20 text-cyan-400',
  bulk_status_update: 'bg-orange-500/20 text-orange-400',
  uploaded_jd: 'bg-pink-500/20 text-pink-400',
  updated: 'bg-yellow-500/20 text-yellow-400',
  imported: 'bg-teal-500/20 text-teal-400',
};

export default function SuperAdminActivityLogs() {
  const [data, setData] = useState({ logs: [], tenant_summaries: [], total_logs: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const location = useLocation();

  // Reset to "All Tenants" whenever the user clicks the sidebar's "Activity Logs"
  // link while already on this page. React Router doesn't unmount the component
  // when navigating to the same path, so we listen for the `key` change instead.
  useEffect(() => {
    setSelectedTenant('all');
  }, [location.key]);

  useEffect(() => { fetchLogs(); }, [selectedTenant]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('superAdminToken');
      const params = selectedTenant !== 'all' ? `?tenant_slug=${selectedTenant}&limit=200` : '?limit=200';
      const res = await axios.get(`${API}/admin-tenant/all/activity-logs${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Group logs by tenant for the organized view
  const logsByTenant = {};
  data.logs.forEach(log => {
    const key = log.tenant_slug || 'unknown';
    if (!logsByTenant[key]) logsByTenant[key] = { name: log.tenant_name, logs: [] };
    logsByTenant[key].logs.push(log);
  });

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Activity Logs
          </h1>
          <p className="text-slate-400 mt-1">
            Complete activity log across all tenants
            {data.total_logs > 0 && <span className="ml-2 text-slate-500">({data.total_logs} entries)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedTenant} onValueChange={setSelectedTenant}>
            <SelectTrigger className="w-56 bg-slate-800 border-slate-600 text-white" data-testid="tenant-filter">
              <Filter className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter by tenant" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
              <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Tenants</SelectItem>
              {data.tenant_summaries.map(t => (
                <SelectItem key={t.slug} value={t.slug}>
                  {t.name} ({t.log_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchLogs} className="border-slate-600 text-slate-300 hover:text-white" data-testid="refresh-logs-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tenant Summary Cards */}
      {selectedTenant === 'all' && data.tenant_summaries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {data.tenant_summaries.map(t => (
            <Card
              key={t.slug}
              className={`bg-slate-900 border-slate-700 cursor-pointer transition-colors hover:border-indigo-500 ${selectedTenant === t.slug ? 'border-indigo-500' : ''}`}
              onClick={() => setSelectedTenant(t.slug)}
              data-testid={`tenant-summary-${t.slug}`}
            >
              <CardContent className="p-3 text-center">
                <Building2 className="h-4 w-4 mx-auto text-indigo-400 mb-1" />
                <p className="text-sm font-medium text-white truncate">{t.name}</p>
                <p className="text-xl font-bold text-indigo-400">{t.log_count}</p>
                <p className="text-xs text-slate-500">activities</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>
      ) : selectedTenant !== 'all' ? (
        /* Single tenant view: flat list */
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-400" />
              {logsByTenant[selectedTenant]?.name || selectedTenant} - Activity
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelectedTenant('all')} className="text-slate-400 hover:text-white text-xs">
              Show All Tenants
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {data.logs.map((log, i) => (
                <LogRow key={`supe-${i}`} log={log} showTenant={false} formatTime={formatTime} />
              ))}
              {data.logs.length === 0 && <p className="text-slate-500 text-sm py-8 text-center">No activity logs found</p>}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* All tenants view: grouped by tenant */
        <div className="space-y-4">
          {Object.entries(logsByTenant).map(([slug, { name, logs }]) => (
            <Card key={slug} className="bg-slate-900 border-slate-700" data-testid={`tenant-logs-${slug}`}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-indigo-400" />
                  {name}
                  <Badge className="bg-slate-800 text-slate-300 text-xs">{logs.length}</Badge>
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTenant(slug)} className="text-indigo-400 hover:text-indigo-300 text-xs">
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {logs.slice(0, 8).map((log, i) => (
                    <LogRow key={`supe-${i}`} log={log} showTenant={false} formatTime={formatTime} />
                  ))}
                  {logs.length > 8 && <p className="text-xs text-slate-500 pt-2 text-center">+{logs.length - 8} more entries</p>}
                </div>
              </CardContent>
            </Card>
          ))}
          {Object.keys(logsByTenant).length === 0 && (
            <Card className="bg-slate-900 border-slate-700"><CardContent className="py-16 text-center text-slate-500">No activity logs across any tenant</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}

function LogRow({ log, showTenant, formatTime }) {
  const actionColor = ACTION_COLORS[log.action] || 'bg-slate-500/20 text-slate-400';
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded hover:bg-slate-800/50 transition-colors text-sm">
      <Badge className={`${actionColor} text-xs px-2 py-0.5 min-w-[70px] text-center`}>
        {log.action}
      </Badge>
      <span className="font-medium text-white truncate max-w-[140px]">{log.user_name}</span>
      <span className="text-slate-500 truncate flex-1">{log.entity_type}{log.details ? ` — ${log.details}` : ''}</span>
      {showTenant && <Badge className="bg-slate-700 text-slate-300 text-xs">{log.tenant_name}</Badge>}
      <span className="text-xs text-slate-500 flex-shrink-0 flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {formatTime(log.timestamp)}
      </span>
    </div>
  );
}
